// CoachingEngine - Rule-based pattern detection for immediate essay feedback
// Provides instant coaching feedback while AI analysis is processing

class CoachingEngine {
  // Main analysis function
  static analyzeText(text) {
    if (!text || typeof text !== 'string' || text.length < 50) {
      return {
        overallScore: 0,
        issues: [],
        strengths: [],
        suggestions: [],
        patterns: {}
      };
    }

    const analysis = {
      wordCount: this.getWordCount(text),
      sentences: this.getSentences(text),
      paragraphs: this.getParagraphs(text)
    };

    const patterns = {
      genericPhrases: this.detectGenericPhrases(text),
      cliches: this.detectCliches(text),
      passiveVoice: this.detectPassiveVoice(text),
      weakOpeners: this.detectWeakOpeners(text),
      showVsTell: this.analyzeShowVsTell(text),
      specificity: this.analyzeSpecificity(text),
      repetition: this.detectRepetition(text),
      transitions: this.analyzeTransitions(text),
      conclusion: this.analyzeConclusion(text)
    };

    const issues = this.generateIssues(patterns, analysis);
    const strengths = this.generateStrengths(patterns, analysis);
    const suggestions = this.generateSuggestions(patterns, analysis);
    const overallScore = this.calculateOverallScore(patterns, analysis);

    return {
      overallScore,
      issues,
      strengths,
      suggestions,
      patterns,
      analysis,
      timestamp: new Date().toISOString()
    };
  }

  // Basic text analysis
  static getWordCount(text) {
    return text.trim().split(/\s+/).filter(w => w.length > 0).length;
  }

  static getSentences(text) {
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    return {
      count: sentences.length,
      averageLength: sentences.reduce((sum, s) => sum + this.getWordCount(s), 0) / sentences.length,
      items: sentences.map(s => s.trim())
    };
  }

  static getParagraphs(text) {
    const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0);
    return {
      count: paragraphs.length,
      averageLength: paragraphs.reduce((sum, p) => sum + this.getWordCount(p), 0) / paragraphs.length,
      items: paragraphs.map(p => p.trim())
    };
  }

  // Pattern detection methods
  static detectGenericPhrases(text) {
    const genericPhrases = [
      'throughout my life',
      'since i was young',
      'growing up',
      'i have always',
      'from an early age',
      'my passion for',
      'i am passionate about',
      'this experience taught me',
      'i learned that',
      'this made me realize',
      'it was then that i knew',
      'at that moment',
      'this journey has taught me',
      'i want to make a difference',
      'help people',
      'change the world',
      'follow my reachs',
      'achieve my goals',
      'overcome obstacles',
      'face challenges',
      'step out of my comfort zone',
      'be the change',
      'give back to society',
      'make an impact'
    ];

    const found = [];
    const lowerText = text.toLowerCase();

    genericPhrases.forEach(phrase => {
      const regex = new RegExp(`\\b${phrase.replace(/\s/g, '\\s+')}\\b`, 'gi');
      const matches = text.match(regex);
      if (matches) {
        found.push({
          phrase,
          count: matches.length,
          severity: this.getGenericPhraseSeverity(phrase),
          suggestions: this.getGenericPhraseAlternatives(phrase)
        });
      }
    });

    return {
      count: found.length,
      items: found,
      severity: found.length > 3 ? 'high' : found.length > 1 ? 'medium' : 'low'
    };
  }

  static detectCliches(text) {
    const cliches = [
      'a blessing in disguise',
      'better late than never',
      'actions speak louder than words',
      'at the end of the day',
      'think outside the box',
      'it goes without saying',
      'easier said than done',
      'when life gives you lemons',
      'what doesn\'t kill you makes you stronger',
      'everything happens for a reason',
      'follow your heart',
      'chase your reachs',
      'the sky is the limit',
      'reach for the stars',
      'life is a journey',
      'opened my eyes',
      'changed my perspective',
      'a wake-up call',
      'a turning point',
      'life-changing experience'
    ];

    const found = [];
    const lowerText = text.toLowerCase();

    cliches.forEach(cliche => {
      if (lowerText.includes(cliche)) {
        found.push({
          cliche,
          severity: 'medium',
          suggestion: 'Try to express this idea in your own unique words'
        });
      }
    });

    return {
      count: found.length,
      items: found,
      severity: found.length > 2 ? 'high' : found.length > 0 ? 'medium' : 'low'
    };
  }

  static detectPassiveVoice(text) {
    const passivePatterns = [
      /\b(was|were|is|are|been|being)\s+\w+ed\b/gi,
      /\b(was|were|is|are|been|being)\s+\w+en\b/gi
    ];

    let passiveCount = 0;
    const examples = [];

    passivePatterns.forEach(pattern => {
      const matches = text.match(pattern);
      if (matches) {
        passiveCount += matches.length;
        examples.push(...matches.slice(0, 3)); // Limit examples
      }
    });

    const sentences = this.getSentences(text);
    const passivePercentage = (passiveCount / sentences.count) * 100;

    return {
      count: passiveCount,
      percentage: passivePercentage,
      examples: examples.slice(0, 5),
      severity: passivePercentage > 20 ? 'high' : passivePercentage > 10 ? 'medium' : 'low'
    };
  }

  static detectWeakOpeners(text) {
    const sentences = this.getSentences(text);
    const weakOpeners = [
      'there is',
      'there are',
      'it is',
      'it was',
      'this is',
      'this was',
      'i think',
      'i believe',
      'in my opinion',
      'i feel that'
    ];

    const found = [];
    sentences.items.forEach((sentence, index) => {
      const lowerSentence = sentence.toLowerCase().trim();
      weakOpeners.forEach(opener => {
        if (lowerSentence.startsWith(opener)) {
          found.push({
            sentence: sentence.substring(0, 100) + (sentence.length > 100 ? '...' : ''),
            opener,
            position: index + 1,
            suggestion: 'Try starting with a stronger, more active construction'
          });
        }
      });
    });

    return {
      count: found.length,
      items: found,
      severity: found.length > 3 ? 'medium' : 'low'
    };
  }

  static analyzeShowVsTell(text) {
    const tellIndicators = [
      'i am',
      'i was',
      'he is',
      'she is',
      'they are',
      'it is important',
      'it is significant',
      'this shows that',
      'this proves that',
      'this demonstrates'
    ];

    const showIndicators = [
      'said',
      'whispered',
      'shouted',
      'exclaimed',
      'muttered',
      'gesturing',
      'pointing',
      'smiling',
      'frowning',
      'laughing',
      'crying',
      'running',
      'walking',
      'stumbling'
    ];

    let tellCount = 0;
    let showCount = 0;

    tellIndicators.forEach(indicator => {
      const regex = new RegExp(`\\b${indicator}\\b`, 'gi');
      const matches = text.match(regex);
      if (matches) tellCount += matches.length;
    });

    showIndicators.forEach(indicator => {
      const regex = new RegExp(`\\b${indicator}\\b`, 'gi');
      const matches = text.match(regex);
      if (matches) showCount += matches.length;
    });

    const ratio = showCount > 0 ? tellCount / showCount : tellCount;

    return {
      tellCount,
      showCount,
      ratio,
      balance: ratio > 3 ? 'too much telling' : ratio < 0.5 ? 'good showing' : 'balanced',
      severity: ratio > 3 ? 'medium' : 'low'
    };
  }

  static analyzeSpecificity(text) {
    const vague = [
      'things',
      'stuff',
      'something',
      'someone',
      'somewhere',
      'somehow',
      'very',
      'really',
      'quite',
      'pretty',
      'many',
      'some',
      'several',
      'various',
      'different',
      'good',
      'bad',
      'nice',
      'great',
      'awesome',
      'amazing'
    ];

    const specific = [
      /\$[\d,]+/g, // dollar amounts
      /\d+\s*(?:years?|months?|weeks?|days?|hours?|minutes?)/gi, // time periods
      /\d+(?:st|nd|rd|th)/gi, // ordinals
      /\d+\s*(?:percent|%)/gi, // percentages
      /[A-Z][a-z]+\s+[A-Z][a-z]+/g // proper names
    ];

    let vagueCount = 0;
    let specificCount = 0;

    // Count vague words
    vague.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      const matches = text.match(regex);
      if (matches) vagueCount += matches.length;
    });

    // Count specific details
    specific.forEach(pattern => {
      const matches = text.match(pattern);
      if (matches) specificCount += matches.length;
    });

    const wordCount = this.getWordCount(text);
    const vaguePercentage = (vagueCount / wordCount) * 100;
    const specificPercentage = (specificCount / wordCount) * 100;

    return {
      vagueCount,
      specificCount,
      vaguePercentage,
      specificPercentage,
      ratio: specificCount > 0 ? vagueCount / specificCount : vagueCount,
      severity: vaguePercentage > 5 ? 'medium' : specificPercentage > 2 ? 'low' : 'medium'
    };
  }

  static detectRepetition(text) {
    const words = text.toLowerCase().match(/\b\w+\b/g) || [];
    const wordCounts = {};
    
    // Count word frequency
    words.forEach(word => {
      if (word.length > 3) { // Skip short words
        wordCounts[word] = (wordCounts[word] || 0) + 1;
      }
    });

    // Find overused words
    const overused = Object.entries(wordCounts)
      .filter(([word, count]) => count > 3 && !this.isCommonWord(word))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    return {
      overusedWords: overused.map(([word, count]) => ({
        word,
        count,
        suggestion: 'Consider using synonyms or varying your vocabulary'
      })),
      severity: overused.length > 3 ? 'medium' : 'low'
    };
  }

  static analyzeTransitions(text) {
    const goodTransitions = [
      'however',
      'moreover',
      'furthermore',
      'nevertheless',
      'consequently',
      'therefore',
      'meanwhile',
      'specifically',
      'for instance',
      'in contrast',
      'on the other hand',
      'as a result'
    ];

    const weakTransitions = [
      'also',
      'and',
      'but',
      'so',
      'then',
      'next'
    ];

    let goodCount = 0;
    let weakCount = 0;

    goodTransitions.forEach(transition => {
      const regex = new RegExp(`\\b${transition}\\b`, 'gi');
      const matches = text.match(regex);
      if (matches) goodCount += matches.length;
    });

    weakTransitions.forEach(transition => {
      const regex = new RegExp(`\\b${transition}\\b`, 'gi');
      const matches = text.match(regex);
      if (matches) weakCount += matches.length;
    });

    const paragraphs = this.getParagraphs(text);
    const transitionDensity = (goodCount + weakCount) / paragraphs.count;

    return {
      goodTransitions: goodCount,
      weakTransitions: weakCount,
      density: transitionDensity,
      quality: goodCount > weakCount ? 'good' : 'needs improvement',
      severity: transitionDensity < 0.5 ? 'medium' : 'low'
    };
  }

  static analyzeConclusion(text) {
    const paragraphs = this.getParagraphs(text);
    if (paragraphs.count === 0) return { present: false, severity: 'high' };

    const lastParagraph = paragraphs.items[paragraphs.items.length - 1].toLowerCase();
    
    const weakConclusionPhrases = [
      'in conclusion',
      'to conclude',
      'in summary',
      'to summarize',
      'finally',
      'lastly',
      'all in all',
      'overall'
    ];

    const hasWeakOpener = weakConclusionPhrases.some(phrase => 
      lastParagraph.startsWith(phrase)
    );

    const repeatsIntroduction = this.checksForRepetition(paragraphs.items[0], lastParagraph);

    return {
      present: true,
      hasWeakOpener,
      repeatsIntroduction,
      length: this.getWordCount(lastParagraph),
      severity: hasWeakOpener || repeatsIntroduction ? 'medium' : 'low'
    };
  }

  // Helper methods for pattern analysis
  static getGenericPhraseSeverity(phrase) {
    const highSeverity = [
      'throughout my life',
      'i have always',
      'my passion for',
      'change the world',
      'make a difference'
    ];
    return highSeverity.includes(phrase) ? 'high' : 'medium';
  }

  static getGenericPhraseAlternatives(phrase) {
    const alternatives = {
      'throughout my life': ['During my childhood', 'Over the past few years', 'Since [specific time]'],
      'i have always': ['I first discovered', 'I developed', 'I began to'],
      'my passion for': ['My interest in', 'My fascination with', 'My commitment to'],
      'change the world': ['improve my community', 'solve specific problems', 'contribute to'],
      'make a difference': ['create meaningful change', 'address specific challenges', 'contribute to']
    };
    return alternatives[phrase] || ['Try being more specific'];
  }

  static isCommonWord(word) {
    const commonWords = [
      'that', 'with', 'have', 'this', 'will', 'they', 'from', 'been', 'said', 
      'each', 'which', 'their', 'time', 'would', 'there', 'could', 'other'
    ];
    return commonWords.includes(word);
  }

  static checksForRepetition(intro, conclusion) {
    const introWords = new Set(intro.toLowerCase().split(/\s+/));
    const conclusionWords = new Set(conclusion.toLowerCase().split(/\s+/));
    
    const overlap = [...introWords].filter(word => 
      conclusionWords.has(word) && word.length > 4
    );
    
    return overlap.length > 3;
  }

  // Issue and suggestion generation
  static generateIssues(patterns, analysis) {
    const issues = [];

    if (patterns.genericPhrases.severity === 'high') {
      issues.push({
        type: 'genericPhrases',
        severity: 'high',
        message: 'Essay contains many generic phrases that could apply to any student',
        count: patterns.genericPhrases.count,
        priority: 1
      });
    }

    if (patterns.cliches.severity === 'high') {
      issues.push({
        type: 'cliches',
        severity: 'medium',
        message: 'Multiple clichés detected - try expressing ideas in fresh ways',
        count: patterns.cliches.count,
        priority: 2
      });
    }

    if (patterns.passiveVoice.percentage > 20) {
      issues.push({
        type: 'passiveVoice',
        severity: 'medium',
        message: 'Heavy use of passive voice makes writing less engaging',
        percentage: Math.round(patterns.passiveVoice.percentage),
        priority: 3
      });
    }

    if (patterns.specificity.vaguePercentage > 5) {
      issues.push({
        type: 'specificity',
        severity: 'medium',
        message: 'Essay needs more specific details and concrete examples',
        percentage: Math.round(patterns.specificity.vaguePercentage),
        priority: 2
      });
    }

    return issues;
  }

  static generateStrengths(patterns, analysis) {
    const strengths = [];

    if (patterns.specificity.specificPercentage > 3) {
      strengths.push({
        type: 'specificity',
        message: 'Good use of specific details and concrete examples',
        evidence: `${Math.round(patterns.specificity.specificPercentage)}% specific content`
      });
    }

    if (patterns.showVsTell.balance === 'good showing') {
      strengths.push({
        type: 'showVsTell',
        message: 'Effective use of showing rather than telling',
        evidence: 'Strong narrative techniques'
      });
    }

    if (patterns.transitions.quality === 'good') {
      strengths.push({
        type: 'transitions',
        message: 'Smooth transitions between ideas',
        evidence: `${patterns.transitions.goodTransitions} sophisticated transitions`
      });
    }

    if (analysis.sentences.averageLength > 12 && analysis.sentences.averageLength < 25) {
      strengths.push({
        type: 'sentenceVariety',
        message: 'Good sentence length variety',
        evidence: `Average sentence length: ${Math.round(analysis.sentences.averageLength)} words`
      });
    }

    return strengths;
  }

  static generateSuggestions(patterns, analysis) {
    const suggestions = [];

    if (patterns.genericPhrases.count > 0) {
      suggestions.push({
        type: 'genericPhrases',
        priority: 'high',
        suggestion: 'Replace generic phrases with specific, personal details',
        example: 'Instead of "throughout my life," try "during my sophomore year" or "last summer"'
      });
    }

    if (patterns.passiveVoice.percentage > 15) {
      suggestions.push({
        type: 'passiveVoice',
        priority: 'medium',
        suggestion: 'Convert passive voice to active voice for stronger impact',
        example: 'Change "mistakes were made by me" to "I made mistakes"'
      });
    }

    if (patterns.specificity.vaguePercentage > 4) {
      suggestions.push({
        type: 'specificity',
        priority: 'high',
        suggestion: 'Add concrete details, numbers, names, and specific examples',
        example: 'Replace "many people" with "fifteen volunteers" or specific names'
      });
    }

    if (analysis.paragraphs.count < 3) {
      suggestions.push({
        type: 'structure',
        priority: 'medium',
        suggestion: 'Consider breaking content into more focused paragraphs',
        example: 'Aim for 4-5 paragraphs with clear topic sentences'
      });
    }

    return suggestions;
  }

  static calculateOverallScore(patterns, analysis) {
    let score = 70; // Base score

    // Deduct for issues
    score -= patterns.genericPhrases.count * 3;
    score -= patterns.cliches.count * 2;
    score -= Math.max(0, patterns.passiveVoice.percentage - 15);
    score -= Math.max(0, patterns.specificity.vaguePercentage - 3) * 2;

    // Add for strengths
    score += Math.min(10, patterns.specificity.specificPercentage);
    score += patterns.transitions.goodTransitions * 2;

    // Structural bonuses
    if (analysis.paragraphs.count >= 4 && analysis.paragraphs.count <= 6) {
      score += 5;
    }

    if (analysis.wordCount >= 400 && analysis.wordCount <= 650) {
      score += 5;
    }

    return Math.max(0, Math.min(100, Math.round(score)));
  }
  
  // New method to detect intentional stylistic patterns
  static detectIntentionalPatterns(text) {
    const patterns = {
      parallelStructure: false,
      anaphora: false,
      epistrophe: false,
      emphasis: false,
      repeatedWords: []
    };
    
    const sentences = text.split(/[.!?]+/).filter(s => s.trim());
    const textLower = text.toLowerCase();
    
    // Check for parallel structure (e.g., "Instead of X, I Y")
    const parallelPatterns = [
      /instead of .+, .+ instead of/i,
      /not .+ but .+/i,
      /neither .+ nor .+/i,
      /either .+ or .+/i,
      /both .+ and .+/i,
      /first .+, then .+, finally/i
    ];
    
    for (const pattern of parallelPatterns) {
      if (pattern.test(textLower)) {
        patterns.parallelStructure = true;
        break;
      }
    }
    
    // Check for anaphora (repetition at beginning)
    if (sentences.length >= 2) {
      const firstWords = sentences.map(s => 
        s.trim().toLowerCase().split(/\s+/)[0]
      ).filter(w => w);
      
      const firstWordCounts = {};
      firstWords.forEach(word => {
        firstWordCounts[word] = (firstWordCounts[word] || 0) + 1;
      });
      
      Object.entries(firstWordCounts)
        .filter(([_, count]) => count >= 2)
        .forEach(([word]) => {
          patterns.anaphora = true;
          if (!patterns.repeatedWords.includes(word)) {
            patterns.repeatedWords.push(word);
          }
        });
    }
    
    return patterns;
  }
  
  // Method to validate time specificity
  static validateTimeSpecificity(text) {
    const specificTimePatterns = [
      { pattern: /\d+ (weeks?|days?|months?|years?) ago/i, specificity: 'high' },
      { pattern: /yesterday|today|tomorrow/i, specificity: 'high' },
      { pattern: /at \d+:\d+/i, specificity: 'high' },
      { pattern: /recently|lately|soon/i, specificity: 'low' },
      { pattern: /one day|once|sometime/i, specificity: 'low' }
    ];
    
    const findings = [];
    
    specificTimePatterns.forEach(({ pattern, specificity }) => {
      const matches = text.match(new RegExp(pattern, 'gi'));
      if (matches) {
        matches.forEach(match => {
          findings.push({
            text: match,
            specificity,
            suggestion: specificity === 'low' 
              ? `Consider replacing "${match}" with a more specific timeframe`
              : null
          });
        });
      }
    });
    
    return {
      hasSpecificTime: findings.some(f => f.specificity === 'high'),
      hasVagueTime: findings.some(f => f.specificity === 'low'),
      findings
    };
  }
}

export default CoachingEngine;